import { Spacing } from '@persona-id/design-tokens';
import { FocusEvent, useCallback, useEffect, useRef } from 'react';
import Markdown from 'shared/components/Markdown';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { isDisabled, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconCheckboxChecked from 'images/icons/inquiry/checkbox-checked.svg';
import IconCheckboxIndeterminate from 'images/icons/inquiry/checkbox-indeterminate.svg';
import IconCheckboxUnchecked from 'images/icons/inquiry/checkbox-unchecked.svg';
import useToggle from 'lib/hooks/useToggle';

const Container = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-wrap: none;
  cursor: pointer;
`;

const HiddenInput = styled.input`
  height: 1px;
  width: 1px;
  margin: -1px;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  position: absolute;
`;

const IconContainer = styled.div<{ focused: boolean; disabled: boolean }>`
  display: flex;
  align-items: center;
  transition: box-shadow ${TransitionDuration.Fast};

  ${({ theme }) => css`
    svg {
      .checkbox__border--empty {
        fill: none;
        stroke: ${theme.colorFont};
      }
      .checkbox__border {
        fill: ${theme.colorButtonPrimary || theme.colorPrimary};
        stroke: ${theme.colorButtonPrimary || theme.colorPrimary};
      }
      .checkbox__check {
        stroke: ${theme.colorFontButtonPrimary};
      }
    }

    &${isHovered}${isNotDisabled} {
      box-shadow:
        0 0 0 3px transparent,
        ${Elevation.Mid};
    }
    &${isFocused}${isNotDisabled} {
      svg {
        .checkbox__border--empty {
          stroke: ${theme.colorButtonPrimary || theme.colorPrimary};
        }
      }
    }
    &${isDisabled} {
      cursor: not-allowed;

      svg {
        .checkbox__border--empty {
          fill: none;
          stroke: ${COLORS.gray20};
        }
        .checkbox__border {
          fill: ${COLORS.gray20};
          stroke: ${COLORS.gray20};
        }
        .checkbox__check {
          stroke: ${theme.colorFontButtonPrimary};
        }
      }
    }
  `}
`;

const StyledMarkdown = styled(Markdown)<{ disabled: boolean }>`
  cursor: inherit;
  padding: 0 ${Spacing[2]};

  ${({ theme, disabled }) =>
    disabled &&
    css`
      color: ${theme.color.text.disabled};
    `}
`;

export type CheckboxProps = {
  className?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  value: boolean | null | undefined | '';
  id?: string;
  label: string;
  autoFocus?: boolean;
  'data-test'?: string;
};

const Checkbox = ({
  className,
  disabled,
  indeterminate,
  onChange,
  onFocus,
  onBlur,
  value,
  id,
  label,
  autoFocus,
  'data-test': dataTest,
}: CheckboxProps) => {
  const [focused, { enable: setFocus, disable: clearFocus }] = useToggle();

  const handleChange = useCallback(() => {
    onChange(!value);
    inputRef.current?.focus();
  }, [onChange, value]);

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (inputRef.current) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <Container
      id={id}
      className={className}
      onClick={disabled ? undefined : handleChange}
      data-test={dataTest}
      role='checkbox'
      aria-checked={!!value}
      aria-readonly={disabled}
    >
      <HiddenInput
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={inputRef}
        data-test-toggle-status={value ? 'on' : 'off'}
        disabled={disabled}
        type='checkbox'
        checked={!!value}
        readOnly={true}
        onChange={() => null}
        autoFocus={autoFocus}
        onFocus={(e) => {
          setFocus();
          onFocus?.(e);
        }}
        onBlur={(e) => {
          clearFocus();
          onBlur?.(e);
        }}
      />
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <IconContainer focused={focused} disabled={disabled}>
        {indeterminate ? (
          <IconCheckboxIndeterminate />
        ) : value ? (
          <IconCheckboxChecked />
        ) : (
          <IconCheckboxUnchecked />
        )}
      </IconContainer>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <StyledMarkdown wrapper='label' wrapperProps={{ htmlFor: id }} disabled={disabled}>
        {label}
      </StyledMarkdown>
    </Container>
  );
};

export default Checkbox;
